import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CalendarPlus,
  CheckCircle2,
  Eye,
  FilePlus2,
  PlayCircle,
  Pen,
  RefreshCw,
  Users,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import {
  getAppointmentsByDateRange,
  getMyDay,
  getPatient,
  getQueueToday,
  type FollowUpDue,
  queueCompleteConsultation,
  isReportReturn,
  type QueueEntry,
  type ReadingCode,
  type QueueTodayResponse,
  queueStartConsultation,
} from '@/lib/api';
import { formatWait } from '@/lib/formatDate';
import { format } from 'date-fns';
import PatientProfileModal from '@/components/Patient/PatientProfileModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import GettingStartedCard from '@/components/GettingStartedCard';
import VitalsWarningChips, { vitalsWarnings } from '@/components/Vitals/VitalsWarningChips';
import FeatureTipCard from '@/components/FeatureTipCard';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { PAGE_ACTIONS_CLASS } from '@/components/ui/page-header';
import { cn } from '@/lib/utils';
import { displayAge } from '@/lib/patientAge';
import { flagFor, fmtNum, patientAgeMonths } from '@/lib/vitals';
import { OPEN_SEARCH_EVENT, RX_PICK_QUERY } from '@/components/SearchOverlay';
import { FollowUpRow, MissedFollowUps, SeenTodayCard, StartVisitCard } from '@/components/Home/DoctorDay';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import { PRINT_PRESCRIPTION } from '@/components/Queue/ConsultationControls';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'completed' | 'cancelled' | 'no_show';
  visit_type?: 'consultation' | 'follow_up' | 'check_up' | 'emergency';
  notes?: string;
  created_at: string;
  updated_at: string;
  // Inline patient fields from API
  patient_name?: string;
  patient_age?: number;
  patient_gender?: string;
  patient_phone?: string;
}

interface AppointmentWithPatientData extends Appointment {
  patientName: string;
  patientAge: number;
  patientGender: string;
  patientPhone: string;
  appointmentTime: Date;
  isToday: boolean;
  isPast: boolean;
  isUpcoming: boolean;
}

interface DashboardStats {
  patientsSeenToday: number;
  totalAppointments: number;
  pendingAppointments: number;
  cancelledAppointments: number;
}

// ─── Pure helpers (no component state deps) ───────────────────────────────────

function getAppointmentTiming(apt: Appointment) {
  try {
    const now = new Date();
    const dateOnly = apt.date.split('T')[0];
    const aptDateTime = new Date(`${dateOnly} ${apt.time}`);
    const isToday = aptDateTime.toDateString() === now.toDateString();
    const timeDiff = aptDateTime.getTime() - now.getTime();
    return {
      isToday,
      isPast: timeDiff < 0 && isToday,
      isUpcoming: timeDiff > 0 && isToday,
      appointmentTime: aptDateTime,
    };
  } catch {
    return { isToday: false, isPast: false, isUpcoming: false, appointmentTime: new Date() };
  }
}

function formatTime(timeString: string) {
  if (!timeString) return 'Not specified';
  try {
    const [h, m] = timeString.split(':').map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return timeString;
  }
}

const VISIT_TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation', follow_up: 'Follow-up',
  check_up: 'Check-up', emergency: 'Emergency',
};

function getVisitTypeLabel(t?: string) { return VISIT_TYPE_LABELS[t ?? ''] || 'General Visit'; }

function calcWaitingTime(entry: QueueEntry) {
  if (entry.waitingTimeMinutes == null) return 'Waiting';
  if (entry.waitingTimeMinutes < 5) return 'Just arrived';
  return `Waiting ${formatWait(entry.waitingTimeMinutes)}`;
}

// Row tint and status word per appointment state
function appointmentTone(apt: AppointmentWithPatientData, isImminent: boolean) {
  if (apt.status === 'completed')   return { row: '', text: 'text-success', label: 'Completed' };
  if (apt.status === 'cancelled')   return { row: '', text: 'text-muted-foreground', label: 'Cancelled' };
  if (apt.status === 'scheduled' && apt.isPast)   return { row: '', text: 'text-muted-foreground', label: 'Missed' };
  if (apt.status === 'scheduled' && isImminent)   return { row: '', text: 'text-warning', label: 'Soon' };
  const label = apt.status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  return { row: '', text: 'text-muted-foreground', label };
}

// Schedule row styling when the patient is already in today's queue
function queueTone(entry: QueueEntry) {
  if (entry.status === 'in_consultation') return { row: 'bg-primary/[0.06]', text: 'text-primary', label: 'In consultation' };
  if (entry.status === 'on_hold')         return { row: '', text: 'text-warning', label: 'On hold · test' };
  if (entry.status === 'completed')       return { row: '', text: 'text-success', label: 'Seen' };
  return { row: '', text: 'text-primary', label: 'In queue' };
}

// Wait-time colour: amber from 15 min, red from 30 (same thresholds as the Front Desk)
function waitTone(mins: number) {
  if (mins >= 30) return 'text-destructive';
  if (mins >= 15) return 'text-warning';
  return 'text-muted-foreground';
}


// ─── Component ────────────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { currentUser } = useAuth();

  useEffect(() => { document.title = 'Dashboard | OneCheckUp'; }, []);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPatientProfileModal, setShowPatientProfileModal] = useState<AppointmentWithPatientData | null>(null);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [myQueue, setMyQueue] = useState<QueueTodayResponse | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  // The doctor's prescriptions today and follow-ups due — the home for doctors working without a front desk
  const [myDay, setMyDay] = useState<{ today: string; seen: any[]; followUps: FollowUpDue[] } | null>(null);
  const [printRx, setPrintRx] = useState<any | null>(null);
  // "Print Rx" on the Consultation ended message (the doctor lands here after ending)
  useEffect(() => {
    const open = (e: Event) => setPrintRx((e as CustomEvent).detail);
    window.addEventListener(PRINT_PRESCRIPTION, open);
    return () => window.removeEventListener(PRINT_PRESCRIPTION, open);
  }, []);

  // Show doctor dashboard if user has clinical profile (doctor_id), regardless of role
  const isDoctor = !!currentUser?.doctor_id;
  const { can } = usePermission();
  const canBook = can('appointments:write');
  const canPrescribe = isDoctor && can('prescriptions:write');
  const canReadRx = isDoctor && can('prescriptions:read');

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadDashboardData = useCallback(async () => {
    try {
      if (!currentUser?.clinic_id) {
        setAppointments([]);
        setMyQueue(null);
        return;
      }
      const today = format(new Date(), 'yyyy-MM-dd');
      const doctorFilter = currentUser.doctor_id ?? undefined;
      const [appointmentsData, queueData, dayData] = await Promise.all([
        getAppointmentsByDateRange(today, today, currentUser.clinic_id, doctorFilter),
        currentUser.doctor_id ? getQueueToday(currentUser.doctor_id, currentUser.clinic_id) : Promise.resolve(null),
        // The rest of the home still loads if this fails
        canReadRx ? getMyDay(currentUser.clinic_id).catch(() => null) : Promise.resolve(null),
      ]);
      if (appointmentsData) setAppointments(appointmentsData);
      setMyQueue(queueData ?? null);
      setMyDay(dayData ?? null);
    } catch (err) {
      handleError(err, 'Dashboard Loading');
    } finally {
      setInitialized(true);
    }
  }, [currentUser?.clinic_id, currentUser?.role, currentUser?.doctor_id, canReadRx]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // Patient in consultation — allergies (never a click away), long-term conditions and sex for the card
  const consultPatientId = myQueue?.entries.find(e => e.status === 'in_consultation')?.patientId ?? null;
  const [consultPatient, setConsultPatient] = useState<{ allergies: string | null; conditions: string | null; gender: string | null } | null>(null);
  useEffect(() => {
    setConsultPatient(null);
    if (!consultPatientId) return;
    let cancelled = false;
    getPatient(consultPatientId)
      .then(p => {
        if (!cancelled) setConsultPatient({
          allergies: p?.allergies?.trim() || null,
          conditions: p?.chronic_conditions?.trim() || null,
          gender: p?.gender || null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consultPatientId]);


  // Poll every 60 s; skip when tab is hidden so background tabs don't waste calls
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadDashboardData();
    }, 60_000);
    return () => clearInterval(id);
  }, [loadDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // ── Parsing — uses inline patient data from API ────────────────────────────

  const parseAppointmentData = useCallback((apt: Appointment): AppointmentWithPatientData => {
    const timing = getAppointmentTiming(apt);
    return {
      ...apt,
      patientName:   apt.patient_name   || 'Unknown',
      patientAge:    apt.patient_age    || 0,
      patientGender: apt.patient_gender || '',
      patientPhone:  apt.patient_phone  || '',
      ...timing,
    };
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────

  const todayAppointmentsWithStatus = useMemo<AppointmentWithPatientData[]>(() =>
    appointments
      .map(parseAppointmentData)
      .sort((a, b) => a.appointmentTime.getTime() - b.appointmentTime.getTime()),
  [appointments, parseAppointmentData]);

  const stats: DashboardStats = useMemo(() => ({
    patientsSeenToday:    todayAppointmentsWithStatus.filter(a => a.status === 'completed').length,
    totalAppointments:    todayAppointmentsWithStatus.length,
    pendingAppointments:  todayAppointmentsWithStatus.filter(a => a.status === 'scheduled').length,
    cancelledAppointments: todayAppointmentsWithStatus.filter(a => a.status === 'cancelled' || a.status === 'no_show').length,
  }), [todayAppointmentsWithStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAppointmentAction = useCallback((apt: AppointmentWithPatientData, action: 'start' | 'profile') => {
    if (!apt?.id) return;
    // Starting opens the prescription; saving it completes today's booking (the server does that)
    if (action === 'start') navigate(`/patients/${apt.patient_id}?openPrescription=true`);
    else setShowPatientProfileModal(apt);
  }, [navigate]);

  const handleQueueStart = useCallback(async (entry: QueueEntry) => {
    if (!entry.patientId) {
      toast({ title: 'Error', description: 'No patient linked to this queue entry.', variant: 'destructive' });
      return;
    }
    try {
      setQueueActionId(entry.id);
      // Call and Resume wait until the current consultation has ended, so this never ends anyone's visit
      await queueStartConsultation(entry.id);
      toast({
        title: entry.status === 'on_hold' ? 'Consultation resumed' : 'Consultation started',
        description: `Calling ${entry.patientName} (Token #${entry.tokenNumber})`,
      });
      await loadDashboardData();
      navigate(`/patients/${entry.patientId}?openPrescription=true`);
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  }, [loadDashboardData, navigate]);

  const handleQueueComplete = useCallback(async (entry: QueueEntry) => {
    try {
      setQueueActionId(entry.id);
      await queueCompleteConsultation(entry.id);
      toast({ title: 'Consultation ended', description: `${entry.patientName} — Token #${entry.tokenNumber}` });
      await loadDashboardData();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  }, [loadDashboardData]);

  const handleKPICardClick = () => {
    navigate('/appointments');
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      // A prescription needs a patient: search opens on "New Rx for …" with the recent patients
      case 'write-prescription': window.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT, { detail: RX_PICK_QUERY })); break;
      case 'add-appointment':    setShowAddAppointmentModal(true); break;
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (!initialized) {
    return (
      <div className="prescription-form-container" aria-busy="true" aria-label="Loading dashboard">
        <div className="rounded-xl border border-border bg-card h-64 mb-6">
          <div className="p-7 space-y-4 animate-pulse">
            <div className="h-3 w-40 rounded bg-muted" />
            <div className="h-9 w-80 max-w-full rounded bg-muted" />
            <div className="h-3 w-64 max-w-full rounded bg-muted/70" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 h-80 rounded-xl bg-muted/60 animate-pulse" />
          <div className="lg:col-span-2 h-80 rounded-xl bg-muted/60 animate-pulse" />
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const liveEntries = myQueue?.entries.filter(e => e.status !== 'cancelled') ?? [];
  const inConsultation = liveEntries.find(e => e.status === 'in_consultation');
  const waitingEntries = liveEntries.filter(e => e.status === 'waiting');
  const onHoldEntries = liveEntries.filter(e => e.status === 'on_hold');
  const nextUp = waitingEntries[0];
  const remainingWaiting = waitingEntries.slice(1);
  const showQueue = isDoctor && !!myQueue;
  const queueActive = !!inConsultation || waitingEntries.length > 0 || onHoldEntries.length > 0;
  const longestWait = waitingEntries.reduce((m, e) => Math.max(m, e.waitingTimeMinutes ?? 0), 0);
  const hasSideQueue = showQueue && (remainingWaiting.length > 0 || onHoldEntries.length > 0);

  // A schedule entry whose patient is already in today's queue shows the queue state, not "Missed"
  const queueByAppointment = new Map(liveEntries.filter(e => e.appointmentId).map(e => [e.appointmentId!, e]));
  const queueByPatient = new Map(liveEntries.filter(e => e.patientId).map(e => [e.patientId!, e]));
  const scheduleRows = todayAppointmentsWithStatus.map(apt => {
    const entry = (apt.status === 'scheduled' || apt.status === 'checked_in')
      ? queueByAppointment.get(apt.id) ?? queueByPatient.get(apt.patient_id)
      : undefined;
    const isImminent = apt.isUpcoming && apt.appointmentTime.getTime() - Date.now() <= 1_800_000;
    const tone = entry ? queueTone(entry) : appointmentTone(apt, isImminent);
    const done = entry
      ? entry.status === 'completed'
      : apt.status === 'completed' || apt.status === 'cancelled' || apt.status === 'no_show';
    return { apt, entry, isImminent, tone, done };
  });
  const upcomingRows = scheduleRows.filter(r => !r.done);
  const doneRows = scheduleRows.filter(r => r.done);
  // The doctor sees arrived patients in the queue cards, so "Expected today" keeps only those not here yet
  const expectedRows = showQueue ? upcomingRows.filter(r => !r.entry) : upcomingRows;
  const shownRows = showDone ? scheduleRows : expectedRows;
  const nowIndex = (() => {
    const i = shownRows.findIndex(r => r.apt.appointmentTime.getTime() > Date.now());
    return i === -1 ? shownRows.length : i;
  })();

  // "Seen" counts every patient whose visit finished today — through the queue, a completed appointment,
  // or a prescription written straight from the patient's page — once each
  const seenRx = myDay?.seen ?? [];
  // One row per patient (the newest prescription), so the card counts the same patients as "seen"
  const seenRxLatest = seenRx.filter((rx, i) => seenRx.findIndex(r => r.patient_id === rx.patient_id) === i);
  const seenPatients = new Set<string>([
    ...liveEntries.filter(e => e.status === 'completed' && e.patientId).map(e => e.patientId!),
    ...todayAppointmentsWithStatus.filter(a => a.status === 'completed').map(a => a.patient_id),
    ...seenRx.map(rx => rx.patient_id),
  ]);
  const seenToday = isDoctor ? seenPatients.size : stats.patientsSeenToday;
  // Follow-ups due today join the day's expected patients (unless they're also booked); older ones are "missed"
  const bookedPatients = new Set(todayAppointmentsWithStatus.map(a => a.patient_id));
  const followUps = myDay?.followUps ?? [];
  const followUpsToday = followUps.filter(f => f.due_date.slice(0, 10) === myDay?.today && !bookedPatients.has(f.patient_id));
  const missedFollowUps = followUps.filter(f => f.due_date.slice(0, 10) < (myDay?.today ?? ''));
  const stillToCome = upcomingRows.filter(r => !r.entry).length;
  const dayLoad = seenToday + (inConsultation ? 1 : 0) + waitingEntries.length + onHoldEntries.length + stillToCome;
  // What to say when nobody is in consultation — specific to why the queue is idle
  const nextBooked = upcomingRows.find(r => !r.entry && r.apt.appointmentTime.getTime() > Date.now())?.apt;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const idleTitle = onHoldEntries.length ? 'Waiting on test results'
    : seenToday > 0 && !nextBooked ? 'All patients seen'
    : 'No patients waiting';
  const idleBody = onHoldEntries.length ? `${plural(onHoldEntries.length, 'patient')} on hold. Resume when they're back from their test.`
    : nextBooked ? `Next booked: ${nextBooked.patientName} at ${formatTime(nextBooked.time)}. Front Desk will add them when they arrive.`
    : seenToday > 0 ? `${plural(seenToday, 'patient')} seen today and nothing else booked.`
    : 'Front Desk will add patients to your queue as they arrive.';
  const nothingToday = seenToday === 0 && waitingEntries.length === 0 && onHoldEntries.length === 0
    && !inConsultation && stats.totalAppointments === 0 && followUps.length === 0;
  const toGo = expectedRows.length + followUpsToday.length;
  // "Done" is visits finished; cancelled and no-show bookings are counted in the line under Today instead
  const finished = doneRows.filter(r => r.apt.status !== 'cancelled' && r.apt.status !== 'no_show').length;
  const scheduleMeta = scheduleRows.length + followUpsToday.length === 0 ? undefined
    : toGo === 0 ? (finished ? `All ${finished} done` : 'Nothing left to see')
    : finished === 0 ? `${toGo} to go`
    : `${toGo} to go · ${finished} done`;
  // The queue's cards take the top of the page while it has patients; otherwise starting a visit does
  const queueBusy = showQueue && queueActive;
  const showStartVisit = canPrescribe && !queueBusy;
  const jumpTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const consultAppt = inConsultation
    ? todayAppointmentsWithStatus.find(a => a.patient_id === inConsultation.patientId)
    : undefined;

  const dateLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  const minutesSince = (iso: string | null) => iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : null;
  const consultMins = inConsultation ? minutesSince(inConsultation.consultationStartAt) : null;
  const apptFor = (e: QueueEntry) =>
    todayAppointmentsWithStatus.find(a => a.id === e.appointmentId) ?? todayAppointmentsWithStatus.find(a => a.patient_id === e.patientId);
  const ageOf = (e: QueueEntry) => displayAge(e.patientAge ?? undefined, e.patientDateOfBirth ?? undefined);
  const nextAppt = nextUp ? apptFor(nextUp) : undefined;

  const headerActions = (
    <div className={cn("flex items-center gap-2", PAGE_ACTIONS_CLASS)}>
      <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} title="Refresh" aria-label="Refresh" className="w-8 px-0">
        <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
      </Button>
      {canBook && (
        <Button variant="outline" size="sm" onClick={() => handleQuickAction('add-appointment')} className="gap-1.5" title="Book an appointment">
          <CalendarPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Book appointment</span>
        </Button>
      )}
      {canPrescribe && !showStartVisit && (
        <Button variant="outline" size="sm" onClick={() => handleQuickAction('write-prescription')} className="gap-1.5" title="Start a blank prescription">
          <FilePlus2 className="w-4 h-4" />
          <span className="hidden sm:inline">Blank prescription</span>
        </Button>
      )}
    </div>
  );

  // One status line under "Today" — what the day looks like and where the pressure is
  const figure = (value: React.ReactNode, cls = 'text-foreground') => <span className={cn('font-semibold tabular-nums', cls)}>{value}</span>;
  const statusParts: React.ReactNode[] = [<span key="date">{dateLabel}</span>];
  if (nothingToday) {
    statusParts.push(<span key="none">No patients yet</span>);
  } else {
    statusParts.push(canReadRx
      ? <button key="seen" type="button" onClick={() => jumpTo('seen-today')} className="hover:text-foreground hover:underline underline-offset-2">{figure(seenToday)} seen</button>
      : <span key="seen">{figure(seenToday)} seen</span>);
    if (queueBusy) statusParts.push(<span key="waiting">{figure(waitingEntries.length)} waiting</span>);
    if (showQueue && longestWait > 0) {
      const tone = waitTone(longestWait);
      statusParts.push(<span key="longest">longest wait {figure(formatWait(longestWait), tone === 'text-muted-foreground' ? 'text-foreground' : tone)}</span>);
    }
    if (showQueue && onHoldEntries.length > 0) statusParts.push(<span key="hold">{figure(onHoldEntries.length)} on hold</span>);
    // Booked and cancelled counts are the front desk's; the doctor works from the queue
    if (!isDoctor && stats.totalAppointments > 0) {
      statusParts.push(
        <button key="booked" type="button" onClick={handleKPICardClick} className="hover:text-foreground hover:underline underline-offset-2">
          {figure(stats.totalAppointments)} booked
        </button>
      );
    }
    if (!isDoctor && stats.cancelledAppointments > 0) statusParts.push(<span key="cancelled">{figure(stats.cancelledAppointments)} cancelled / no-show</span>);
    if (followUpsToday.length > 0) {
      statusParts.push(
        <button key="follow-ups" type="button" onClick={() => jumpTo('schedule')} className="hover:text-foreground hover:underline underline-offset-2">
          {figure(followUpsToday.length)} follow-up{followUpsToday.length === 1 ? '' : 's'} due
        </button>
      );
    }
  }

  return (
    <div className="prescription-form-container">

      {/* ── Today ─────────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="text-[22px] leading-7 tracking-[-0.01em] font-semibold text-foreground">Today</h1>
          <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
            {statusParts.map((part, i) => (
              <React.Fragment key={i}>{i > 0 && <span aria-hidden>·</span>}{part}</React.Fragment>
            ))}
          </p>
        </div>
        {headerActions}
      </div>

      {/* ── In consultation + Next up — or Start a visit while the queue is quiet ── */}
      {showStartVisit && <StartVisitCard />}
      {showQueue && !showStartVisit && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5">
          {/* With nobody in consultation, Next up alone says who to call */}
          {(inConsultation || !nextUp) && (
          <section
            aria-label="In consultation"
            className={cn('rounded-lg border border-border bg-card px-4 sm:px-5 py-4 min-w-0', inConsultation && nextUp ? 'lg:col-span-3' : 'lg:col-span-5')}
          >
            {inConsultation ? (
              <>
                <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <span className="h-2 w-2 rounded-full bg-primary" aria-hidden />
                  In consultation{consultMins != null ? ` · ${formatWait(consultMins)}` : ''}
                </p>
                <div className="mt-3 flex items-center gap-4 sm:gap-5">
                  <p className="font-mono text-4xl font-semibold leading-none text-primary shrink-0" aria-label={`Token ${inConsultation.tokenNumber}`}>
                    {inConsultation.tokenNumber}
                  </p>
                  <div className="min-w-0 border-l border-border pl-4 sm:pl-5 space-y-1.5">
                    <button
                      type="button"
                      onClick={() => navigate(`/patients/${inConsultation.patientId}`)}
                      className="block max-w-full text-left text-xl font-semibold leading-tight text-foreground truncate capitalize hover:underline underline-offset-2"
                    >
                      {inConsultation.patientName}
                    </button>
                    <p className="text-sm text-muted-foreground truncate">
                      {[
                        ageOf(inConsultation),
                        consultPatient?.gender,
                        consultAppt?.reason || null,
                        consultAppt?.visit_type ? getVisitTypeLabel(consultAppt.visit_type) : null,
                      ].filter(Boolean).join(' · ') || 'Walk-in'}
                    </p>
                    {consultPatient && (
                      <ul aria-label="Safety information" className="flex flex-wrap items-center gap-1.5">
                        {consultPatient.allergies ? (
                          <li className="inline-flex items-center gap-1 max-w-[22rem] rounded border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive" title={`Allergies: ${consultPatient.allergies}`}>
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
                            <span className="truncate">Allergy: {consultPatient.allergies}</span>
                          </li>
                        ) : (
                          <li className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">No allergies recorded</li>
                        )}
                        {consultPatient.conditions && (
                          <li className="max-w-[18rem] truncate rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80" title={consultPatient.conditions}>
                            {consultPatient.conditions}
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                <ConsultVitals entry={inConsultation} />

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {canPrescribe && (
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/patients/${inConsultation.patientId}?openPrescription=true`)}>
                      <Pen className="w-3.5 h-3.5" /> Open prescription
                    </Button>
                  )}
                  {/* Ending comes first: the next patient is called only once this consultation has ended */}
                  <Button
                    size="sm" className="gap-1.5"
                    onClick={() => handleQueueComplete(inConsultation)}
                    disabled={queueActionId !== null}
                  >
                    {queueActionId === inConsultation.id ? 'Ending…' : <><CheckCircle2 className="w-3.5 h-3.5" /> End consultation</>}
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-1">
                <p className="text-sm font-semibold text-muted-foreground">Your queue</p>
                <p className="mt-1 text-lg font-semibold text-foreground">{idleTitle}</p>
                <p className="mt-1 text-sm text-muted-foreground">{idleBody}</p>
              </div>
            )}
          </section>
          )}

          {(inConsultation || nextUp) && (
            <section aria-label="Next up" className={cn(inConsultation ? 'lg:col-span-2' : 'lg:col-span-5', 'rounded-lg border border-border bg-card px-4 sm:px-5 py-4 flex flex-col min-w-0')}>
              {nextUp ? (
                <>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-muted-foreground">Next up</p>
                    <div className="mt-3 flex items-center gap-4">
                      <p className="font-mono text-3xl font-semibold leading-none text-foreground shrink-0" aria-label={`Token ${nextUp.tokenNumber}`}>{nextUp.tokenNumber}</p>
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-foreground truncate capitalize">{nextUp.patientName}</p>
                        {isReportReturn(nextUp) && <StatusChip tone="accent">Back with report</StatusChip>}
                        <p className={cn('text-xs font-medium', waitTone(nextUp.waitingTimeMinutes ?? 0))}>{calcWaitingTime(nextUp)}</p>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {[ageOf(nextUp), nextUp.patientGender,nextUp.vitalsRecordedAt ? 'Vitals recorded' : 'No vitals yet'].filter(Boolean).join(' · ')}
                    </p>
                    {nextAppt?.reason && (
                      <p className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                        Reason: {nextAppt.reason}
                      </p>
                    )}
                    <VitalsWarningChips entry={nextUp} className="mt-2" />
                  </div>
                  <Button onClick={() => handleQueueStart(nextUp)} disabled={queueActionId !== null || !!inConsultation} className="mt-4 w-full gap-1.5">
                    {queueActionId === nextUp.id
                      ? 'Calling…'
                      : <>Call #{nextUp.tokenNumber} <ArrowRight className="w-4 h-4" /></>}
                  </Button>
                  {inConsultation && (
                    <p className="mt-1.5 text-center text-xs text-muted-foreground">End #{inConsultation.tokenNumber}'s consultation first</p>
                  )}
                </>
              ) : (
                <p className="my-auto text-sm text-muted-foreground">
                  {onHoldEntries.length ? `No one else waiting · ${onHoldEntries.length} on hold` : 'No one else waiting.'}
                </p>
              )}
            </section>
          )}
        </div>
      )}

      {/* Staff: FrontDesk shortcut */}
      {!isDoctor && (
        <button
          type="button"
          onClick={() => navigate('/frontdesk')}
          className="w-full flex items-center justify-between gap-4 px-4 py-3 mb-5 rounded-lg border border-border bg-card text-left hover:bg-muted/40 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Front Desk</p>
              <p className="text-xs text-muted-foreground">Queue management, walk-ins, check-ins</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-primary" />
        </button>
      )}

      {/* Left: who is still coming (waiting, then expected today). Right: who has been seen */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5 items-start">
        <div className={cn('min-w-0 space-y-4', canReadRx ? 'lg:col-span-3' : 'lg:col-span-5')}>

        {/* ── Waiting — everyone after "Next up", then patients on hold ── */}
        {hasSideQueue && (
          <section aria-label="Waiting" className="rounded-lg border border-border bg-card overflow-hidden min-w-0">
            <div className="flex items-center h-11 px-4 border-b border-border">
              <h2 className="text-[15px] font-semibold text-foreground">
                Waiting
                <span className="font-normal text-muted-foreground tabular-nums">
                  {' · '}{remainingWaiting.length}{onHoldEntries.length > 0 ? ` · ${onHoldEntries.length} on hold` : ''}
                </span>
              </h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="h-8 border-b border-border bg-muted/40 text-2xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="pl-4 pr-2 text-left font-medium w-16 hidden sm:table-cell">Token</th>
                  <th scope="col" className="px-2 text-left font-medium">Patient</th>
                  <th scope="col" className="px-2 text-left font-medium hidden md:table-cell">Reason</th>
                  <th scope="col" className="px-2 text-right font-medium">Waiting</th>
                  <th scope="col" className="px-2 text-left font-medium hidden sm:table-cell">Vitals</th>
                  <th scope="col" className="pl-2 pr-4"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {remainingWaiting.map(entry => (
                  <tr key={entry.id} className="h-10 border-b border-divider last:border-0">
                    <td className="pl-4 pr-2 font-mono font-semibold text-foreground hidden sm:table-cell" aria-label={`Token ${entry.tokenNumber}`}>#{entry.tokenNumber}</td>
                    <td className="pl-4 sm:pl-2 pr-2">
                      <span className="block max-w-[11rem] sm:max-w-[12rem] truncate text-foreground capitalize">
                        <span className="sm:hidden mr-1.5 font-mono font-semibold">#{entry.tokenNumber}</span>
                        {entry.patientName}
                        {ageOf(entry) && <span className="ml-2 text-xs text-muted-foreground normal-case">{ageOf(entry)}</span>}
                        {isReportReturn(entry) && <StatusChip tone="accent" className="ml-2">Report</StatusChip>}
                      </span>
                    </td>
                    <td className="px-2 hidden md:table-cell">
                      <span className="block max-w-[14rem] truncate text-muted-foreground">{apptFor(entry)?.reason || '—'}</span>
                    </td>
                    <td className={cn('px-2 text-right font-mono tabular-nums whitespace-nowrap', waitTone(entry.waitingTimeMinutes ?? 0))}>
                      {entry.waitingTimeMinutes != null ? formatWait(entry.waitingTimeMinutes) : '—'}
                    </td>
                    <td className="px-2 hidden sm:table-cell"><QueueVitals entry={entry} /></td>
                    <td className="pl-2 pr-4" />
                  </tr>
                ))}
                {onHoldEntries.map(entry => (
                  <tr key={entry.id} className="h-10 border-b border-divider last:border-0">
                    <td className="pl-4 pr-2 font-mono font-semibold text-foreground hidden sm:table-cell" aria-label={`Token ${entry.tokenNumber}`}>#{entry.tokenNumber}</td>
                    <td className="pl-4 sm:pl-2 pr-2">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 py-1">
                        <span className="max-w-[10rem] truncate text-foreground capitalize">
                          <span className="sm:hidden mr-1.5 font-mono font-semibold">#{entry.tokenNumber}</span>
                          {entry.patientName}
                        </span>
                        <StatusChip tone="warning">On hold · test</StatusChip>
                      </span>
                    </td>
                    <td className="px-2 hidden md:table-cell">
                      <span className="block max-w-[14rem] truncate text-muted-foreground">{apptFor(entry)?.reason || '—'}</span>
                    </td>
                    <td className="px-2 text-right text-muted-foreground">—</td>
                    <td className="px-2 hidden sm:table-cell"><QueueVitals entry={entry} /></td>
                    <td className="pl-2 pr-4 text-right">
                      <Button
                        variant="outline" size="sm" className="h-7 px-2.5 gap-1 text-xs"
                        onClick={() => handleQueueStart(entry)}
                        // Like Call: waits until the current consultation has ended
                        disabled={queueActionId !== null || !!inConsultation}
                        title={inConsultation ? `End #${inConsultation.tokenNumber}'s consultation first` : undefined}
                        aria-label={`Resume #${entry.tokenNumber} ${entry.patientName}`}
                      >
                        <PlayCircle className="w-3.5 h-3.5" />
                        {queueActionId === entry.id ? 'Resuming…' : 'Resume'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </section>
        )}

        {/* ── Expected today: bookings, then follow-ups due; finished visits fold away, missed follow-ups last ── */}
        <section id="schedule" aria-label={isDoctor ? 'Expected today' : 'Schedule'} className="rounded-lg border border-border bg-card overflow-hidden min-w-0 scroll-mt-4">
          <div className="flex items-center justify-between gap-3 h-11 px-4 border-b border-border">
            <h2 className="text-[15px] font-semibold text-foreground">
              {isDoctor ? 'Expected today' : 'Schedule'}{scheduleMeta && <span className="font-normal text-muted-foreground tabular-nums"> · {scheduleMeta}</span>}
            </h2>
            <button onClick={() => navigate('/appointments')} className="touch-target inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline underline-offset-2">
              All appointments <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {scheduleRows.length + followUpsToday.length > 0 ? (
            <>
              <ol>
                {shownRows.map(({ apt, entry, isImminent, tone, done }, i) => {
                  const cancelled = apt.status === 'cancelled';
                  return (
                    <React.Fragment key={apt.id}>
                      {i === nowIndex && <NowMarker />}
                      <li className={cn('flex items-center gap-3 px-4 py-1.5 min-h-10 border-b border-divider', tone.row)}>
                        <span className={cn('w-16 shrink-0 font-mono text-xs tabular-nums', done ? 'text-muted-foreground' : 'text-foreground')}>
                          {formatTime(apt.time)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className={cn('text-sm truncate capitalize', done ? 'text-muted-foreground' : 'text-foreground', cancelled && 'line-through decoration-muted-foreground/60')}>
                            {apt.patientName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            <span className={cn('font-medium', tone.text)}>{tone.label}</span>
                            {[
                              entry ? `#${entry.tokenNumber}` : null,
                              apt.visit_type ? getVisitTypeLabel(apt.visit_type) : null,
                              apt.reason || null,
                            ].filter(Boolean).map(s => ` · ${s}`).join('')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!cancelled && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleAppointmentAction(apt, 'profile')} title="View patient profile" aria-label={`View ${apt.patientName}'s profile`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Patients already in the queue are handled from the queue, not from here */}
                          {canPrescribe && !entry && apt.status === 'scheduled' && (
                            <Button variant={isImminent ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 text-xs" onClick={() => handleAppointmentAction(apt, 'start')}>
                              Start
                            </Button>
                          )}
                        </div>
                      </li>
                    </React.Fragment>
                  );
                })}
                {nowIndex === shownRows.length && shownRows.length > 0 && <NowMarker />}
                {followUpsToday.map(f => <FollowUpRow key={f.prescription_id} followUp={f} canPrescribe={canPrescribe} />)}
              </ol>
              {doneRows.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDone(v => !v)}
                  className="w-full h-9 px-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                  aria-expanded={showDone}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {showDone ? 'Hide' : 'Show'} {doneRows.length} finished / cancelled
                </button>
              )}
            </>
          ) : (
            <p className="px-4 py-4 text-sm text-muted-foreground">
              {isDoctor ? 'Nothing booked and no follow-ups due today.' : 'Nothing is booked for today yet.'}
              {canBook && (
                <button type="button" onClick={() => handleQuickAction('add-appointment')} className="ml-1 font-medium text-primary hover:underline underline-offset-2">
                  Book an appointment
                </button>
              )}
            </p>
          )}
          {/* Calling back missed follow-ups is for after clinic, not between patients */}
          {!queueBusy && <MissedFollowUps followUps={missedFollowUps} />}
        </section>
        </div>

        {/* ── Seen today: reprint or share what was written ── */}
        {canReadRx && <SeenTodayCard className="lg:col-span-2 scroll-mt-4" prescriptions={seenRxLatest} onPrint={setPrintRx} />}
      </div>

      {/* Tips at the foot, never pushing the day's work down; not while the doctor is mid-clinic */}
      {!queueBusy && <GettingStartedCard fallback={<FeatureTipCard />} />}

      <PrintPrescriptionPreviewModal open={!!printRx} prescription={printRx} onClose={() => setPrintRx(null)} />

      <PatientProfileModal
        patient={showPatientProfileModal}
        isOpen={!!showPatientProfileModal}
        onClose={() => setShowPatientProfileModal(null)}
        onStartConsultation={apt => handleAppointmentAction(apt as AppointmentWithPatientData, 'start')}
      />

      <AddAppointmentModal
        isOpen={showAddAppointmentModal}
        onClose={() => setShowAddAppointmentModal(false)}
        onAdded={() => {
          loadDashboardData();
          setShowAddAppointmentModal(false);
        }}
        patients={[]}
        showPatientSelection
      />
    </div>
  );
}

// ─── Presentational bits ──────────────────────────────────────────────────────

const VITAL_CELLS: { label: string; code: ReadingCode; unit: string }[] = [
  { label: 'Pulse', code: 'pulse', unit: 'bpm' },
  { label: 'SpO₂', code: 'spo2', unit: '%' },
  { label: 'Temp', code: 'temp', unit: '°F' },
  { label: 'Weight', code: 'weight', unit: 'kg' },
];

/** Today's vitals for the patient in consultation: BP, pulse, SpO₂, temperature, weight. Out-of-range values are coloured. */
function ConsultVitals({ entry }: { entry: QueueEntry }) {
  const vitals = entry.todayVitals ?? [];
  if (!vitals.length) return <p className="mt-4 text-sm text-muted-foreground">No vitals recorded today.</p>;
  const ageMonths = patientAgeMonths({ date_of_birth: entry.patientDateOfBirth, age: entry.patientAge });
  const get = (code: ReadingCode) => vitals.find(v => v.code === code);
  const sys = get('bp_systolic'), dia = get('bp_diastolic');
  const cells = [
    { label: 'BP', unit: 'mmHg', value: sys && dia ? `${fmtNum(sys.value)}/${fmtNum(dia.value)}` : null, flag: flagFor(sys, ageMonths) ?? flagFor(dia, ageMonths), lowIsUrgent: false },
    ...VITAL_CELLS.map(c => {
      const r = get(c.code);
      return { label: c.label, unit: c.unit, value: r ? fmtNum(r.value) : null, flag: flagFor(r, ageMonths), lowIsUrgent: c.code === 'spo2' };
    }),
  ];
  return (
    <dl className="mt-4 grid grid-cols-5 rounded-md border border-border divide-x divide-border">
      {cells.map(c => (
        <div key={c.label} className="px-1.5 sm:px-3 py-2 min-w-0">
          <dt className="text-xs text-muted-foreground">{c.label}</dt>
          <dd className={cn(
            'font-mono text-[13px] sm:text-base font-semibold tabular-nums truncate',
            !c.value ? 'text-muted-foreground'
              : c.flag === 'H' || (c.flag === 'L' && c.lowIsUrgent) ? 'text-destructive'
              : c.flag === 'L' ? 'text-highlight-foreground'
              : 'text-foreground',
          )}>
            {c.value ?? '—'}
            {c.value && <span className="hidden sm:inline ml-1 font-sans text-2xs font-normal text-muted-foreground">{c.unit}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Vitals cell of the waiting table: abnormal readings first, otherwise whether any were taken */
function QueueVitals({ entry }: { entry: QueueEntry }) {
  if (vitalsWarnings(entry).length > 0) return <VitalsWarningChips entry={entry} max={2} />;
  return entry.vitalsRecordedAt
    ? <span className="text-xs text-success">Recorded</span>
    : <span className="text-xs text-muted-foreground">—</span>;
}

function NowMarker() {
  return (
    <li className="flex items-center gap-3 px-4 h-6" aria-label="Current time">
      <span className="w-16 shrink-0 text-2xs font-semibold uppercase tracking-wide text-primary">Now</span>
      <span className="h-px flex-1 bg-primary/50" aria-hidden />
    </li>
  );
}
